// src/features/pulse/LeadershipRadar.jsx
//
// LEADERSHIP-RADAR-PANE-BRIEF-STACK: Decision Brief Stack layout for the Pulse
// home Radar pane.  Two independently scrollable halves at a fixed 560 px height.
//
// Left rail  — "Priority briefs" PriorityBriefCard stack.
// Right pane — CompactMemo executive brief for the selected item.
//
// Surfaces Decisions, Actions, Issues, Expirations, Signals, and Projects
// ranked by the server-side deterministic engine.  Triage actions (snooze,
// resolve, assign) are shown only for editor/admin users; the server enforces
// the same gate with requireRole("editor").
//
// userRole detection:
//   1. If the caller passes `userRole` as a prop (e.g. from tests), that wins.
//   2. Otherwise the component fetches /api/me on mount and derives the role.
//      Default before resolution: "viewer" (safe — triage buttons hidden).

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { T } from "../../app/tokens.js";
import { getRadarFeed, patchRadarTriage } from "../../data/radarClient.js";
import { log } from "../../utils/logger.js";
import { showToast } from "../../components/toastService.jsx";
import { LinkedWorkflowRunsIndicator } from "../workflows/LinkedWorkflowRunsIndicator.jsx";

// ─── Type metadata ────────────────────────────────────────────────────────────

export const TYPE_META = {
  decision: { label: "Decision",   fg: T.radarDecision, bg: T.radarDecisionBg, icon: "⚡" },
  action:   { label: "Action",     fg: T.radarAction,   bg: T.radarActionBg,   icon: "✓" },
  issue:    { label: "Issue",      fg: T.radarIssue,    bg: T.radarIssueBg,    icon: "⚠" },
  expiry:   { label: "Expiration", fg: T.radarExpiry,   bg: T.radarExpiryBg,   icon: "⏱" },
  signal:   { label: "Signal",     fg: T.radarSignal,   bg: T.radarSignalBg,   icon: "◈" },
  project:  { label: "Project",    fg: T.radarProject,  bg: T.radarProjectBg,  icon: "▣" },
};

const TYPE_WEIGHT = { decision: 5, action: 4, issue: 3, expiry: 3, signal: 2, project: 1 };

export const FILTER_TYPES = ["decision", "action", "issue", "expiry", "signal", "project"];
const LEDGER_MAX   = 20;

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function isSaneDate(iso) {
  if (!iso) return false;
  const y = new Date(iso).getFullYear();
  return y > 1970 && y < 2101;
}

export function daysUntilClient(iso) {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(iso);
  return Math.round((due - today) / 86_400_000);
}

function fmtDue(iso) {
  if (!iso || !isSaneDate(iso)) return "—";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due  = new Date(iso);
  const days = Math.round((due - today) / 86_400_000);
  if (days < 0)   return `${Math.abs(days)}d ago`;
  if (days === 0) return "Today";
  if (days <= 7)  return `${days}d`;
  if (days <= 60) return `${Math.ceil(days / 7)}w`;
  return `${Math.ceil(days / 30)}mo`;
}

function fmtDueAbsolute(iso) {
  if (!iso || !isSaneDate(iso)) return null;
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day:   "numeric",
    year:  "numeric",
  });
}

function dueColor(iso) {
  if (!iso || !isSaneDate(iso)) return T.mid;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(iso) - today) / 86_400_000);
  if (days <= 30) return T.radarIssue;
  return T.mid;
}

function ownerInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function whySurfaced(item) {
  const days = daysUntilClient(item.dueDate);
  switch (item.type) {
    case "expiry":
      if (days !== null && days <= 30) return `Expiration in ${days}d · requires immediate action`;
      if (days !== null && days <= 90) return `Expiration within ${days}d · high importance score`;
      return "Lease critical date approaching";
    case "decision":
      if (days !== null && days <= 14) return `Decision deadline in ${days}d - binding choice required`;
      if (days !== null && days <= 30) return `Decision due in ${days}d · needs leadership sign-off`;
      return "Open decision with upcoming deadline";
    case "action":
      if (days !== null && days < 0) return "Action item is overdue";
      if (days !== null && days <= 14) return `Action due in ${days}d · assigned to this portfolio`;
      return "Open action item with deadline approaching";
    case "signal":
      return "Signal requires triage - potential impact on space or people data";
    case "issue":
      return "Portfolio issue flagged for leadership review";
    case "project":
      return "Project milestone approaching - confirm next steps to avoid slippage";
    default:
      return "Surfaced based on due date and importance score";
  }
}

// Client-side sort (mirrors server radarRank.js logic)
export function applySort(items, sortMode) {
  const arr = [...items];
  if (sortMode === "due_soonest") {
    arr.sort((a, b) => {
      const da = daysUntilClient(a.dueDate);
      const db = daysUntilClient(b.dueDate);
      if (da === null && db === null) return (b._score ?? 0) - (a._score ?? 0);
      if (da === null) return 1;
      if (db === null) return -1;
      if (da !== db) return da - db;
      return (b._score ?? 0) - (a._score ?? 0);
    });
  } else if (sortMode === "type") {
    arr.sort((a, b) => {
      const wa = TYPE_WEIGHT[a.type] ?? 0;
      const wb = TYPE_WEIGHT[b.type] ?? 0;
      if (wb !== wa) return wb - wa;
      if ((b._score ?? 0) !== (a._score ?? 0)) return (b._score ?? 0) - (a._score ?? 0);
      return a.itemKey < b.itemKey ? -1 : 1;
    });
  } else {
    // "impact" (default) — score DESC then itemKey ASC (matches server default)
    arr.sort(
      (a, b) => (b._score ?? 0) - (a._score ?? 0) || (a.itemKey < b.itemKey ? -1 : 1),
    );
  }
  return arr;
}

// ─── Status label synthesis ───────────────────────────────────────────────────

function statusLabel(item) {
  if (item.resolved) return "Resolved";
  const days = daysUntilClient(item.dueDate);
  if (days !== null && days < 0) return "Overdue";
  if (days !== null && days <= 7) return "Due soon";
  if (item._seeded) return "Sample";
  return "Open";
}

// ─── Sub-components ───────────────────────────────────────────────────────────

export function FilterChip({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={`radar-chip-${label.toLowerCase().replace(/\s+/g, "-")}`}
      onClick={onClick}
      style={{
        display:     "inline-flex",
        alignItems:  "center",
        gap:         4,
        padding:     "3px 10px",
        fontSize:    T.fsXs,
        fontWeight:  active ? T.fwSemibold : T.fwRegular,
        background:  active ? T.blue : T.lightGray,
        color:       active ? T.white : T.dark,
        border:      "none",
        borderRadius: T.radiusFull,
        cursor:      "pointer",
        whiteSpace:  "nowrap",
        transition:  "background 0.15s",
      }}
    >
      {label}
      {count != null && (
        <span style={{
          background:   active ? "rgba(255,255,255,0.25)" : T.border,
          borderRadius: T.radiusFull,
          padding:      "0 5px",
          fontSize:     T.fsXxs,
          fontWeight:   T.fwBold,
          lineHeight:   "16px",
        }}>
          {count}
        </span>
      )}
    </button>
  );
}

// ─── OwnerAvatar ──────────────────────────────────────────────────────────────

export function OwnerAvatar({ name, size = 22 }) {
  if (!name) return <span style={{ color: T.mid }}>—</span>;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      <span style={{
        display:         "inline-flex",
        alignItems:      "center",
        justifyContent:  "center",
        width:           size,
        height:          size,
        borderRadius:    "50%",
        background:      T.blue,
        color:           T.white,
        fontSize:        T.fsXxs,
        fontWeight:      T.fwBold,
        flexShrink:      0,
        userSelect:      "none",
      }}>
        {ownerInitials(name)}
      </span>
      <span style={{
        fontSize:     T.fsSm,
        color:        T.dark,
        overflow:     "hidden",
        textOverflow: "ellipsis",
        whiteSpace:   "nowrap",
      }}>
        {name}
      </span>
    </div>
  );
}

// ─── KVCell ───────────────────────────────────────────────────────────────────

function KVCell({ label, value }) {
  return (
    <div style={{
      padding:      "12px 13px",
      borderRadius: T.radiusCard,
      background:   T.lightGray,
      border:       `1px solid ${T.border}`,
    }}>
      <div style={{
        display:       "block",
        fontSize:      T.fsXxxs5,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color:         T.light,
        fontWeight:    T.fwBold,
        marginBottom:  5,
      }}>
        {label}
      </div>
      <div style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark }}>
        {value}
      </div>
    </div>
  );
}

// ─── Quiet button style ────────────────────────────────────────────────────────

const quietBtn = {
  padding:      "8px 13px",
  borderRadius: T.radiusSm,
  border:       `1px solid ${T.border}`,
  background:   T.white,
  fontWeight:   T.fwBold,
  fontSize:     T.fsSm5,
  color:        T.mid,
  cursor:       "pointer",
};

// ─── PriorityBriefCard ────────────────────────────────────────────────────────
// Left-rail card in the Brief Stack.
// eyebrow = label text only (no icon) · source sub-label; title; one-line
// whySurfaced sentence; meta pills; right-aligned IMPACT score badge.
// Selected state = T.selBg fill, no border-colour change.

export function PriorityBriefCard({ item, selected, onSelect }) {
  const meta      = TYPE_META[item.type] ?? TYPE_META.signal;
  const why       = whySurfaced(item);
  const score     = Math.round(item._score ?? 0);
  const dueStr    = fmtDue(item.dueDate);
  const isOverdue = isSaneDate(item.dueDate) && daysUntilClient(item.dueDate) < 0;

  const pills = [];
  if (dueStr !== "—") pills.push({ label: dueStr, color: isOverdue ? T.radarIssue : T.mid });
  if (item.owner) pills.push({ label: item.owner.split(" ")[0], color: T.mid });
  if (item._seeded) pills.push({ label: "Sample", color: T.mid });

  return (
    <div
      data-testid={`radar-card-${item.itemKey}`}
      onClick={() => onSelect(item.itemKey)}
      style={{
        background:   selected ? T.selBg : T.white,
        border:       `1px solid ${T.border}`,
        borderRadius: T.radiusCard,
        padding:      "13px 14px",
        marginBottom: 9,
        cursor:       "pointer",
        transition:   "background 0.1s",
        boxShadow:    T.shadowTileXs,
      }}
    >
      <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
        {/* Body */}
        <div style={{ flex: 1 }}>
          {/* Eyebrow: label only (no icon) + · source */}
          <div style={{
            fontSize:      T.fsXxs,
            fontWeight:    T.fwBold,
            color:         meta.fg,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}>
            {meta.label}
            {item.src && (
              <span style={{ fontWeight: T.fwRegular, color: T.mid }}> · {item.src}</span>
            )}
          </div>

          {/* Title */}
          <div style={{
            fontSize:      T.fsMd,
            fontWeight:    T.fwBold,
            color:         T.dark,
            lineHeight:    1.25,
            margin:        "6px 0 5px",
            letterSpacing: "-0.01em",
          }}>
            {item.title}
          </div>

          {/* Why sentence */}
          <div style={{
            fontSize:     T.fsSm,
            color:        T.mid,
            lineHeight:   1.4,
            marginBottom: 9,
          }}>
            {why}
          </div>

          {/* Meta pills */}
          {pills.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {pills.map((p, i) => (
                <span key={i} style={{
                  fontSize:     T.fsXs,
                  padding:      "3px 8px",
                  borderRadius: T.radiusFull,
                  background:   T.lightGray,
                  color:        p.color,
                  fontWeight:   T.fwSemibold,
                }}>
                  {p.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* IMPACT score badge */}
        <div style={{
          minWidth:     52,
          textAlign:    "center",
          borderRadius: T.radiusCard,
          background:   T.lightGray,
          padding:      "7px 6px",
          flexShrink:   0,
        }}>
          <div style={{
            fontSize:      T.fsLg6,
            fontWeight:    T.fwBold,
            letterSpacing: "-0.02em",
            color:         T.dark,
            lineHeight:    1,
          }}>
            {score}
          </div>
          <div style={{
            fontSize:      T.fsXxxs,
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            color:         T.light,
            fontWeight:    T.fwBold,
            marginTop:     2,
          }}>
            impact
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── CompactMemo ──────────────────────────────────────────────────────────────
// Right-pane executive brief. Matches the Decision Brief Stack mockup.
// Sections (in order): kicker + headline + sub-line; confidence badge (top-right);
// "Why it matters" (neutral); "Recommended action" (green); "If delayed" (amber);
// 2×2 KV grid; quiet action buttons (editor-gated); AI grounding note.

export function CompactMemo({ item, isEditor, onTriage, triageError, onNavigate, onStartWorkflow, onOpenLinkedRun }) {
  const [assignMode,  setAssignMode]  = useState(false);
  const [assignInput, setAssignInput] = useState("");

  if (!item) {
    return (
      <div style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        height:         "100%",
        color:          T.light,
        fontSize:       T.fsSm,
        fontStyle:      "italic",
        padding:        24,
        textAlign:      "center",
      }}>
        Select an item to see details
      </div>
    );
  }

  const meta       = TYPE_META[item.type] ?? TYPE_META.signal;
  const absDate    = fmtDueAbsolute(item.dueDate);
  const why        = whySurfaced(item);
  const confidence = item.confidence ?? "Medium";

  const handleSnooze = () => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    onTriage(item.itemKey, { snoozedUntil: d.toISOString() });
  };
  const handleResolve      = () => onTriage(item.itemKey, { resolved: !item.resolved });
  const handleAssignConfirm = () => {
    if (assignInput.trim()) {
      onTriage(item.itemKey, { assignedTo: assignInput.trim() });
    }
    setAssignMode(false);
    setAssignInput("");
  };

  return (
    <div
      data-testid="radar-brief"
      style={{
        padding:   "20px 22px",
        background: T.white,
        overflowY: "auto",
        minHeight: 0,
      }}
    >
      {/* ── Memo top: kicker + headline + sub-line · confidence badge ── */}
      <div style={{
        display:        "flex",
        justifyContent: "space-between",
        gap:            20,
        alignItems:     "flex-start",
        marginBottom:   18,
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {/* Kicker */}
          <div
            data-testid="radar-brief-kicker"
            style={{
              fontSize:      T.fsXs,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              fontWeight:    T.fwBold,
              color:         meta.fg,
              marginBottom:  7,
            }}
          >
            Leadership brief · {meta.label}
          </div>

          {/* Headline */}
          <div
            data-testid="radar-brief-summary"
            style={{
              fontSize:      T.fsLg6,
              fontWeight:    T.fwBold,
              lineHeight:    1.2,
              letterSpacing: "-0.015em",
              color:         T.dark,
              marginBottom:  5,
            }}
          >
            {item.summary ?? item.title}
          </div>

          {/* Sub-line */}
          <div style={{ fontSize: T.fsBase, color: T.mid }}>
            {[item.locationName, item.src ? `Surfaced from ${item.src}` : null]
              .filter(Boolean).join(" · ")}
          </div>
        </div>

        {/* Confidence badge */}
        <div
          data-testid="radar-brief-confidence"
          style={{
            border:      `1px solid ${T.border}`,
            borderRadius: T.radiusLg,
            padding:      "11px 14px",
            textAlign:    "center",
            background:   T.lightGray,
            minWidth:     110,
            flexShrink:   0,
          }}
        >
          <div style={{
            fontSize:   T.fsLg6,
            fontWeight: T.fwBold,
            color:      T.dark,
            lineHeight: 1,
            marginBottom: 3,
          }}>
            {confidence}
          </div>
          <div style={{
            fontSize:      T.fsXxs,
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            color:         T.light,
            fontWeight:    T.fwBold,
          }}>
            confidence
          </div>
        </div>
      </div>

      {/* ── Why it matters (neutral) ── */}
      <div
        data-testid="radar-brief-why-surfaced"
        style={{
          border:       `1px solid ${T.border}`,
          borderRadius: T.radiusLg,
          padding:      "14px 16px",
          marginBottom: 11,
        }}
      >
        <div style={{
          fontSize:      T.fsXxxs5,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontWeight:    T.fwBold,
          color:         T.mid,
          marginBottom:  6,
        }}>
          Why it matters
        </div>
        <div style={{ fontSize: T.fsBase5, lineHeight: 1.45, color: T.mid }}>
          {item.matters ?? why}
        </div>
        <div style={{
          marginTop:  8,
          fontSize:   T.fsXs5,
          fontWeight: T.fwBold,
          color:      T.blue,
        }}>
          ◈ Surfaced by Ripple AI
        </div>
      </div>

      {/* ── Recommended action (green) ── */}
      {item.recommendedMove && (
        <div style={{
          background:   T.radarActionBg,
          border:       `1px solid ${T.radarAction}`,
          borderRadius: T.radiusLg,
          padding:      "14px 16px",
          marginBottom: 11,
        }}>
          <div style={{
            fontSize:      T.fsXxxs5,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight:    T.fwBold,
            color:         T.radarAction,
            marginBottom:  6,
          }}>
            Recommended action
          </div>
          <div style={{ fontSize: T.fsBase5, lineHeight: 1.45, color: T.dark }}>
            {item.recommendedMove}
          </div>
        </div>
      )}

      {/* ── If delayed (amber) ── */}
      {item.ifDelayed && (
        <div style={{
          background:   T.radarExpiryBg,
          border:       `1px solid ${T.radarExpiry}`,
          borderRadius: T.radiusLg,
          padding:      "14px 16px",
          marginBottom: 11,
        }}>
          <div style={{
            fontSize:      T.fsXxxs5,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight:    T.fwBold,
            color:         T.radarExpiry,
            marginBottom:  6,
          }}>
            If delayed
          </div>
          <div style={{ fontSize: T.fsBase5, lineHeight: 1.45, color: T.dark }}>
            {item.ifDelayed}
          </div>
        </div>
      )}

      {/* ── 2×2 KV grid ── */}
      <div style={{
        display:             "grid",
        gridTemplateColumns: "1fr 1fr",
        gap:                 11,
        marginBottom:        11,
        marginTop:           4,
      }}>
        <KVCell label="Suggested owner" value={<OwnerAvatar name={item.owner} />} />
        <KVCell label="Due date" value={absDate ?? "Not set"} />
        <KVCell label="Source" value={item.src ?? meta.label} />
        <KVCell label="Recommended next step" value={item.nextStep ?? "Review"} />
      </div>

      {/* ── Linked-to chips (when navUrl present) ── */}
      {item.navUrl && (
        <div data-testid="radar-brief-linked-chips" style={{ marginBottom: 11 }}>
          <div style={{
            fontSize:     T.fsXxs,
            fontWeight:   T.fwBold,
            color:        T.light,
            marginBottom: 4,
            letterSpacing: 0.4,
          }}>
            LINKED TO
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <a
              href={item.navUrl}
              onClick={(e) => {
                if (onNavigate) { e.preventDefault(); onNavigate(item.navUrl); }
              }}
              style={{
                display:        "inline-flex",
                alignItems:     "center",
                gap:            4,
                padding:        "3px 9px",
                background:     T.blueSoft,
                color:          T.blue,
                border:         `1px solid ${T.blueSoftBorder}`,
                borderRadius:   T.radiusFull,
                fontSize:       T.fsXxs,
                fontWeight:     T.fwSemibold,
                textDecoration: "none",
                whiteSpace:     "nowrap",
              }}
            >
              Open {meta.label} →
            </a>
          </div>
        </div>
      )}

      {/* ── Seeded badge ── */}
      {item._seeded && (
        <div style={{ fontSize: T.fsXxs, color: T.light, fontStyle: "italic", marginBottom: 8 }}>
          Sample data · live feed coming soon
        </div>
      )}

      {/* ── Triage error ── */}
      {triageError && (
        <div
          data-testid="radar-triage-error"
          style={{ fontSize: T.fsXs, color: T.radarIssue, marginBottom: 8 }}
        >
          {triageError}
        </div>
      )}

      {/* ── Linked workflow run indicator — visible to all roles; only the
          Start-workflow button itself is editor/admin gated below. ── */}
      {item.locationId && (
        <div data-testid="radar-linked-run-indicator" style={{ marginTop: 12 }}>
          <LinkedWorkflowRunsIndicator sourceType="decision" sourceId={item.locationId} onOpenRun={onOpenLinkedRun} />
        </div>
      )}

      {/* ── Quiet action buttons — editor/admin only ── */}
      {isEditor && (
        <div data-testid="radar-triage-actions" style={{ marginTop: 16 }}>
          {/* Assign input (shown when assignMode active) */}
          {assignMode && (
            <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
              <input
                data-testid="radar-assign-input"
                value={assignInput}
                onChange={(e) => setAssignInput(e.target.value)}
                placeholder="Owner name or email"
                onKeyDown={(e) => e.key === "Enter" && handleAssignConfirm()}
                style={{
                  flex:         1,
                  fontSize:     T.fsXs,
                  padding:      "5px 8px",
                  border:       `1px solid ${T.border}`,
                  borderRadius: T.radiusSm,
                  outline:      "none",
                }}
              />
              <button
                type="button"
                data-testid="radar-assign-confirm"
                onClick={handleAssignConfirm}
                style={{
                  background:   T.blue,
                  color:        T.white,
                  border:       "none",
                  borderRadius: T.radiusSm,
                  padding:      "5px 10px",
                  fontSize:     T.fsXs,
                  fontWeight:   T.fwSemibold,
                  cursor:       "pointer",
                }}
              >
                Set
              </button>
              <button
                type="button"
                onClick={() => { setAssignMode(false); setAssignInput(""); }}
                style={{
                  background:   T.lightGray,
                  color:        T.mid,
                  border:       "none",
                  borderRadius: T.radiusSm,
                  padding:      "5px 8px",
                  fontSize:     T.fsXs,
                  cursor:       "pointer",
                }}
              >
                ✕
              </button>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => { setAssignMode(true); setAssignInput(item.assignedTo ?? ""); }}
              style={quietBtn}
            >
              Assign owner
            </button>
            <button type="button" style={quietBtn}>
              Create task
            </button>
            {item.navUrl && (
              <button
                type="button"
                data-testid="radar-open-plan"
                onClick={() => onNavigate ? onNavigate(item.navUrl) : (window.location.href = item.navUrl)}
                style={quietBtn}
              >
                Open record
              </button>
            )}
            <button
              type="button"
              onClick={handleResolve}
              style={quietBtn}
            >
              {item.resolved ? "Unresolve" : "Mark as Reviewed"}
            </button>
            <button
              type="button"
              onClick={handleSnooze}
              style={quietBtn}
            >
              Snooze 7d
            </button>
            {onStartWorkflow && item.locationId && (
              <button
                type="button"
                data-testid="radar-start-workflow"
                onClick={() => onStartWorkflow({
                  type: "decision",
                  id: item.locationId,
                  label: item.title || item.summary || meta.label,
                  locationId: item.locationId,
                })}
                style={quietBtn}
              >
                Start action
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── AI grounding note ── */}
      <div style={{
        marginTop:  14,
        color:      T.blue,
        fontSize:   T.fsXs5,
        fontWeight: T.fwBold,
      }}>
        ✦ Surfaced by Ripple AI · grounded in live portfolio data
      </div>
    </div>
  );
}

// ─── LedgerCard (kept for backward compat / RadarPage legacy use) ─────────────

export function LedgerCard({ item, selected, onSelect }) {
  const meta      = TYPE_META[item.type] ?? TYPE_META.signal;
  const stat      = statusLabel(item);
  const dueFg     = dueColor(item.dueDate);
  const dueStr    = fmtDue(item.dueDate);
  const isOverdue = isSaneDate(item.dueDate) && daysUntilClient(item.dueDate) < 0;

  return (
    <div
      data-testid={`radar-card-${item.itemKey}`}
      onClick={() => onSelect(item.itemKey)}
      style={{
        background:   selected ? T.selBg : T.white,
        border:       selected ? `1px solid ${T.selBorder}` : `1px solid ${T.border}`,
        borderRadius: T.radiusCard,
        padding:      "9px 13px",
        cursor:       "pointer",
        transition:   "background 0.1s",
        boxShadow:    T.shadowTileXs,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <span style={{
          fontSize:      T.fsXxs,
          fontWeight:    T.fwBold,
          color:         meta.fg,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          flexShrink:    0,
        }}>
          {meta.icon} {meta.label}
        </span>
        <span style={{
          background:    meta.bg,
          color:         meta.fg,
          border:        `1px solid ${meta.fg}33`,
          borderRadius:  T.radiusFull,
          padding:       "1px 6px",
          fontSize:      T.fsXxs,
          fontWeight:    T.fwBold,
          letterSpacing: "0.03em",
          textTransform: "uppercase",
          flexShrink:    0,
        }}>
          {stat}
        </span>
        <div style={{ marginLeft: "auto", flexShrink: 0 }}>
          {(dueStr !== "—" || item.owner) && (
            <span style={{ fontSize: T.fsSm, whiteSpace: "nowrap" }}>
              {dueStr !== "—" && (
                <span style={{
                  fontWeight: T.fwBold,
                  color:      isOverdue ? T.radarIssue : T.dark,
                }}>
                  {dueStr}
                </span>
              )}
              {dueStr !== "—" && item.owner && (
                <span style={{ color: T.light }}> · </span>
              )}
              {item.owner && (
                <span style={{ color: T.light }}>
                  {item.owner.split(" ")[0]}
                </span>
              )}
            </span>
          )}
        </div>
      </div>
      <div style={{
        fontSize:   T.fsMd,
        fontWeight: T.fwBold,
        color:      T.dark,
        margin:     "4px 0 1px",
        lineHeight: 1.3,
      }}>
        {item.title}
      </div>
      {item.locationName && (
        <div style={{ fontSize: T.fsSm, color: T.mid }}>
          {item.locationName}
        </div>
      )}
      {item.recommendedMove && (
        <div style={{
          fontSize:             T.fsSm,
          color:                T.mid,
          lineHeight:           1.45,
          marginTop:            5,
          display:              "-webkit-box",
          WebkitLineClamp:      2,
          WebkitBoxOrient:      "vertical",
          overflow:             "hidden",
        }}>
          <span style={{ fontWeight: T.fwSemibold, color: T.dark }}>→ </span>
          {item.recommendedMove}
        </div>
      )}
    </div>
  );
}

// ─── LedgerRow (table-row variant — used by full-page Radar view) ─────────────

export function LedgerRow({ item, rank, selected, onSelect }) {
  const meta = TYPE_META[item.type] ?? TYPE_META.signal;
  return (
    <tr
      data-testid={`radar-row-${item.itemKey}`}
      onClick={() => onSelect(item.itemKey)}
      style={{
        cursor:       "pointer",
        background:   selected ? T.hoverBg : "transparent",
        borderBottom: `1px solid ${T.lightGray}`,
      }}
    >
      <td style={{ padding: "7px 8px", width: 36, textAlign: "center" }}>
        <span style={{
          fontSize:   T.fsXxs,
          color:      T.light,
          fontWeight: T.fwBold,
          fontVariantNumeric: "tabular-nums",
        }}>
          {String(rank).padStart(2, "0")}
        </span>
      </td>
      <td style={{ padding: "7px 6px" }}>
        <div style={{
          fontSize:     T.fsSm,
          fontWeight:   T.fwSemibold,
          color:        T.dark,
          whiteSpace:   "nowrap",
          overflow:     "hidden",
          textOverflow: "ellipsis",
          maxWidth:     260,
        }}>
          {item.title}
        </div>
        {item.locationName && (
          <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 1 }}>
            {item.locationName}
          </div>
        )}
      </td>
      <td style={{ padding: "7px 6px", width: 88 }}>
        <span style={{
          display:      "inline-flex",
          alignItems:   "center",
          gap:          3,
          background:   meta.bg,
          color:        meta.fg,
          borderRadius: T.radiusSm,
          padding:      "2px 7px",
          fontSize:     T.fsXxs,
          fontWeight:   T.fwBold,
          whiteSpace:   "nowrap",
        }}>
          {meta.icon} {meta.label}
        </span>
      </td>
      <td style={{ padding: "7px 6px", width: 56, textAlign: "right" }}>
        <span style={{
          fontSize:   T.fsXs,
          fontWeight: T.fwSemibold,
          color:      dueColor(item.dueDate),
          whiteSpace: "nowrap",
        }}>
          {fmtDue(item.dueDate)}
        </span>
      </td>
      <td style={{ padding: "7px 8px", width: 76 }}>
        <span style={{
          fontSize:     T.fsXxs,
          color:        T.mid,
          display:      "block",
          overflow:     "hidden",
          textOverflow: "ellipsis",
          whiteSpace:   "nowrap",
        }}>
          {item.owner ? item.owner.split(" ")[0] : "—"}
        </span>
      </td>
    </tr>
  );
}

// ─── RadarBrief (kept for backward compat) ────────────────────────────────────

export function RadarBrief(props) {
  return <CompactMemo {...props} />;
}

// ─── LeadershipRadar ──────────────────────────────────────────────────────────

export function LeadershipRadar({ style, onNavigate, activeView, userRole: userRoleProp, onStartWorkflow, onOpenLinkedRun }) {
  const [userRole,        setUserRole]        = useState(userRoleProp ?? "viewer");
  const [feed,            setFeed]            = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [selectedKey,     setSelectedKey]     = useState(null);
  const [activeFilter,    setActiveFilter]    = useState("all");
  const [sortMode,        setSortMode]        = useState("impact");
  const [includeResolved, setIncludeResolved] = useState(false);
  const [triageError,     setTriageError]     = useState(null);
  const [reloadKey,       setReloadKey]       = useState(0);

  // Role detection — prop wins; otherwise fetch /api/me
  useEffect(() => {
    if (userRoleProp !== undefined) return;
    fetch("/api/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => { if (u?.role) setUserRole(u.role); })
      .catch(() => {});
  }, [userRoleProp]);

  // Feed load
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    getRadarFeed({ includeResolved })
      .then((data) => {
        if (cancelled) return;
        setFeed(data);
        setLoading(false);
        if (data?.items?.length && !selectedKey) {
          setSelectedKey(data.items[0].itemKey);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        log.error("LeadershipRadar: feed load failed", { err: String(err) });
        setError(err);
        setLoading(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, includeResolved]);

  const allItems = feed?.items ?? [];
  const counts   = feed?.counts ?? {};

  // Client-side filter then sort
  const filteredItems = useMemo(() => {
    let items = allItems;
    if (activeFilter === "due_soon") {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() + 14);
      const iso = cutoff.toISOString().slice(0, 10);
      items = items.filter((i) => i.dueDate && i.dueDate <= iso);
    } else if (activeFilter !== "all") {
      items = items.filter((i) => i.type === activeFilter);
    }
    return applySort(items, sortMode);
  }, [allItems, activeFilter, sortMode]);

  const visibleItems = filteredItems.slice(0, LEDGER_MAX);
  const selectedItem = allItems.find((i) => i.itemKey === selectedKey) ?? null;
  const isEditor     = userRole === "editor" || userRole === "admin";

  const handleSelect = useCallback((key) => {
    setSelectedKey(key);
    setTriageError(null);
  }, []);


  const handleTriage = useCallback(async (itemKey, patch) => {
    setTriageError(null);

    // Optimistic update
    setFeed((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((i) =>
          i.itemKey === itemKey ? { ...i, ...patch } : i
        ),
      };
    });

    try {
      await patchRadarTriage(itemKey, patch);
      // Close the CompactMemo panel
      setSelectedKey(null);
      // Derive a human-readable toast label from the patch
      let toastMsg = "Done";
      if (patch.snoozedUntil)         toastMsg = "Snoozed";
      else if (patch.resolved === true)  toastMsg = "Resolved";
      else if (patch.resolved === false) toastMsg = "Unresolved";
      else if (patch.assignedTo)      toastMsg = `Assigned to ${patch.assignedTo}`;
      showToast(toastMsg);
      setReloadKey((n) => n + 1);
    } catch (err) {
      setReloadKey((n) => n + 1);
      setTriageError(err.message ?? "Triage action failed.");
    }
  }, []);

  return (
    <div
      data-testid="leadership-radar"
      style={{
        background:   T.white,
        border:       `1px solid ${T.border}`,
        borderRadius: T.radiusCard,
        overflow:     "hidden",
        ...style,
      }}
    >
      {/* ── Header ── */}
      <div style={{
        padding:      "18px 22px",
        borderBottom: `1px solid ${T.lightGray}`,
        display:      "flex",
        justifyContent: "space-between",
        alignItems:   "center",
      }}>
        {/* Title block */}
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          {/* Blue accent bar */}
          <div style={{
            width:        3,
            height:       14,
            borderRadius: 2,
            background:   T.blue,
            flexShrink:   0,
            marginTop:    2,
          }} />
          <div>
            <div style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark, lineHeight: 1.2 }}>
              Leadership Radar
            </div>
            <div style={{ fontSize: T.fsSm5, color: T.mid, marginTop: 1 }}>
              A brief-first view that explains why each surfaced item matters.
            </div>
          </div>
        </div>

        {/* "View full Radar →" */}
        <button
          type="button"
          data-testid="radar-view-full-header"
          onClick={() => onNavigate?.("?view=radar")}
          style={{
            background:     "none",
            border:         "none",
            color:          T.blue,
            fontWeight:     T.fwBold,
            fontSize:       T.fsSm5,
            cursor:         onNavigate ? "pointer" : "default",
            padding:        0,
            textDecoration: "none",
            whiteSpace:     "nowrap",
          }}
        >
          View full Radar →
        </button>
      </div>

      {/* ── Filter chips + sort row ── */}
      <div style={{
        padding:    "10px 16px",
        borderBottom: `1px solid ${T.lightGray}`,
        display:    "flex",
        alignItems: "center",
        gap:        5,
        flexWrap:   "wrap",
      }}>
        <FilterChip label="All"      count={allItems.length}  active={activeFilter === "all"}      onClick={() => setActiveFilter("all")} />
        <FilterChip label="Due soon" count={counts.due_soon}  active={activeFilter === "due_soon"} onClick={() => setActiveFilter("due_soon")} />
        {FILTER_TYPES.map((type) => (
          <FilterChip
            key={type}
            label={TYPE_META[type].label + "s"}
            count={counts[type]}
            active={activeFilter === type}
            onClick={() => setActiveFilter(type)}
          />
        ))}

        {/* Sort control */}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: T.fsXs, color: T.light, cursor: "default" }}>Sort:</span>
          <select
            data-testid="radar-sort-control"
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            style={{
              fontSize:     T.fsXs,
              color:        T.dark,
              background:   T.lightGray,
              border:       `1px solid ${T.border}`,
              borderRadius: T.radiusSm,
              padding:      "3px 6px",
              cursor:       "pointer",
              outline:      "none",
            }}
          >
            <option value="impact">Ranked by engine</option>
            <option value="due_soonest">Due soonest</option>
            <option value="type">Type</option>
          </select>

          <button
            type="button"
            data-testid="radar-toggle-resolved"
            onClick={() => setIncludeResolved((v) => !v)}
            style={{
              background:     "none",
              border:         "none",
              fontSize:       T.fsXxs,
              color:          includeResolved ? T.blue : T.light,
              cursor:         "pointer",
              padding:        "2px 4px",
              textDecoration: includeResolved ? "underline" : "none",
            }}
          >
            {includeResolved ? "Hide resolved" : "View resolved"}
          </button>
        </div>
      </div>

      {/* ── Body: left rail (priority briefs) + right pane (compact memo) ── */}
      <div style={{
        display:             "grid",
        gridTemplateColumns: "1fr 1fr",
        height:              560,
      }}>
        {/* ── Left rail ── */}
        <div style={{
          borderRight: `1px solid ${T.border}`,
          background:  T.lightGray,
          padding:     14,
          overflowY:   "auto",
          minHeight:   0,
        }}>
          {loading && (
            <div style={{ padding: 32, color: T.light, fontSize: T.fsSm, textAlign: "center" }}>
              Loading…
            </div>
          )}

          {!loading && error && (
            <div style={{ padding: "24px 16px", color: T.radarIssue, fontSize: T.fsSm }}>
              Failed to load Radar feed.
            </div>
          )}

          {!loading && !error && (
            <>
              {visibleItems.map((item) => (
                <PriorityBriefCard
                  key={item.itemKey}
                  item={item}
                  selected={item.itemKey === selectedKey}
                  onSelect={handleSelect}
                />
              ))}
              {visibleItems.length === 0 && (
                <div style={{ padding: "28px 16px", textAlign: "center", color: T.light, fontSize: T.fsSm }}>
                  No items match the current filter.
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Right pane: compact memo ── */}
        <CompactMemo
          item={selectedItem}
          isEditor={isEditor}
          onTriage={handleTriage}
          triageError={triageError}
          onNavigate={onNavigate}
          onStartWorkflow={onStartWorkflow}
          onOpenLinkedRun={onOpenLinkedRun}
        />
      </div>

    </div>
  );
}
